/**
 * Open-report tabs — switch between several reports, or several views of one report, without
 * losing each one's filters. Lives in the lighter top bar.
 *
 * A second copy of a report is always asked for: the menu goes to the tab already open, and only
 * the duplicate control (or a middle-click, as a browser tab is duplicated) opens another. Tabs
 * that multiply on their own are tabs nobody closes.
 */

import { faClone, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useReportWorkspace } from '@/lib/reportWorkspace';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

export function ReportTabs() {
  const { text } = useI18n();
  const { tabs, activeKey, activateTab, closeTab, duplicateTab } = useReportWorkspace();

  if (tabs.length === 0) return null;

  return (
    <div
      role="tablist"
      aria-label={text('Open reports')}
      className="flex min-h-0 min-w-0 flex-1 items-stretch gap-0.5 overflow-x-auto"
    >
      {tabs.map((tab) => {
        const active = tab.key === activeKey;
        return (
          <div
            key={tab.key}
            role="presentation"
            onAuxClick={(e) => {
              if (e.button !== 1) return;
              // Middle-click scrolls otherwise, and the browser's own duplicate is what a reader
              // expects this gesture to do on a tab.
              e.preventDefault();
              duplicateTab(tab.key);
            }}
            className={cn(
              'group flex max-w-52 min-w-0 shrink-0 items-center gap-0.5 rounded-md px-0.5',
              active
                ? 'bg-shell-top-active text-shell-top-foreground'
                : 'text-shell-top-muted hover:bg-shell-top-hover hover:text-shell-top-foreground',
            )}
          >
            <button
              type="button"
              role="tab"
              aria-selected={active}
              title={tab.description}
              onClick={() => {
                activateTab(tab.key);
              }}
              className={cn(
                'min-w-0 flex-1 truncate px-2 py-1 text-left text-xs',
                active ? 'font-medium' : 'font-normal',
              )}
            >
              {text(tab.label)}
            </button>
            {active ? (
              <button
                type="button"
                aria-label={text('Open another {report}', { report: text(tab.label) })}
                title={text('Open another {report}', { report: text(tab.label) })}
                onClick={(e) => {
                  e.stopPropagation();
                  duplicateTab(tab.key);
                }}
                // Carries the action colour rather than the chrome's muted grey: it is the one
                // control here that makes something, and at this size it disappears otherwise.
                // Close stays grey, so the two are told apart by more than position.
                className={cn(
                  'flex size-5 shrink-0 items-center justify-center rounded text-primary',
                  'hover:bg-primary/10 hover:text-primary',
                )}
              >
                <FontAwesomeIcon icon={faClone} className="size-3" aria-hidden />
              </button>
            ) : null}
            <button
              type="button"
              aria-label={text('Close {report}', { report: text(tab.label) })}
              onClick={(e) => {
                e.stopPropagation();
                closeTab(tab.key);
              }}
              className={cn(
                'flex size-5 shrink-0 items-center justify-center rounded text-shell-top-muted',
                'hover:bg-shell-top-foreground/10 hover:text-shell-top-foreground',
                'opacity-70 group-hover:opacity-100',
                active && 'opacity-100',
              )}
            >
              <FontAwesomeIcon icon={faXmark} className="size-2.5" aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );
}
